import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const CostosObra = () => {
  const theme = useTheme();
  const [selectedObra, setSelectedObra] = useState('pozo-xy-123');

  const costosData = [
    { categoria: 'Mano de Obra', monto: 1850000, porcentaje: 45 },
    { categoria: 'Materiales', monto: 980000, porcentaje: 24 },
    { categoria: 'Equipos', monto: 650000, porcentaje: 16 },
    { categoria: 'Logística', monto: 420000, porcentaje: 10 },
    { categoria: 'Otros', monto: 200000, porcentaje: 5 },
  ];

  const pieColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
  ];

  const comparativoData = [
    { mes: 'Sep', presupuesto: 800, real: 750 },
    { mes: 'Oct', presupuesto: 900, real: 920 },
    { mes: 'Nov', presupuesto: 1000, real: 1050 },
    { mes: 'Dic', presupuesto: 850, real: 880 },
    { mes: 'Ene', presupuesto: 650, real: 500 },
  ];

  const detalleGastos = [
    { fecha: '03/02/2026', concepto: 'Servicio de Soldadura', categoria: 'Mano de Obra', monto: 185000 },
    { fecha: '02/02/2026', concepto: 'Tuberías 4"', categoria: 'Materiales', monto: 320000 },
    { fecha: '01/02/2026', concepto: 'Alquiler Grúa', categoria: 'Equipos', monto: 150000 },
    { fecha: '31/01/2026', concepto: 'Flete Materiales', categoria: 'Logística', monto: 85000 },
    { fecha: '30/01/2026', concepto: 'Combustible', categoria: 'Otros', monto: 45000 },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Costos por Obra
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Análisis detallado de costos y gastos por proyecto
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Seleccionar Obra</InputLabel>
            <Select value={selectedObra} onChange={(e) => setSelectedObra(e.target.value)} label="Seleccionar Obra">
              <MenuItem value="pozo-xy-123">Pozo XY-123</MenuItem>
              <MenuItem value="pozo-ab-456">Pozo AB-456</MenuItem>
              <MenuItem value="workover-mz-789">Workover MZ-789</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<ExportIcon />}>
            Exportar
          </Button>
        </Box>
      </Box>

      {/* Resumen de Costos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Costo Total</Typography>
              <Typography variant="h4" fontWeight={700} color="primary">
                $4.100.000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Presupuesto</Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                $5.200.000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Disponible</Typography>
              <Typography variant="h4" fontWeight={700} color="info.main">
                $1.100.000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Distribución de Costos */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Distribución de Costos
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={costosData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="monto"
                    nameKey="categoria"
                    label={({ categoria, porcentaje }) => `${porcentaje}%`}
                  >
                    {costosData.map((entry, index) => (
                      <Cell key={index} fill={pieColors[index]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                {costosData.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: pieColors[index] }} />
                      <Typography variant="body2">{item.categoria}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      ${item.monto.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Comparativo Mensual */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Presupuesto vs Real (Miles)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={comparativoData}>
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value}K`} />
                  <Legend />
                  <Bar dataKey="presupuesto" name="Presupuesto" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="real" name="Real" fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalle de Gastos */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Últimos Gastos Registrados
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell>Categoría</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalleGastos.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{row.fecha}</TableCell>
                        <TableCell>{row.concepto}</TableCell>
                        <TableCell>
                          <Chip label={row.categoria} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            ${row.monto.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CostosObra;
